import {
  ArrowDownIcon,
  ArrowUpDownIcon,
  ArrowUpIcon,
  EqualIcon,
  ExternalLinkIcon,
  Grid3x3Icon,
  KeyboardIcon,
  Loader2Icon,
  Maximize2Icon,
  MoreHorizontalIcon,
  SparklesIcon,
  Table2Icon,
  TextCursorInputIcon,
  TypeIcon,
  WandSparklesIcon,
} from "lucide-react";
import { Badge } from "@/shared/components/ui/badge";
import { BetaBadge } from "@/shared/components/beta-badge";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { cn } from "@/shared/lib/utils";
import { ComingSoonBadge } from "./coming-soon-badge";

type MatrixInfoDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

// Explains the grid with the grid's own icons and pills.
export function MatrixInfoDialog({
  open,
  onOpenChange,
}: MatrixInfoDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-lg">
        <DialogHeader className="border-b px-6 pt-6 pb-4">
          <DialogTitle className="flex items-center gap-2.5">
            <span className="bg-primary/10 text-primary flex size-8 shrink-0 items-center justify-center rounded-lg">
              <Grid3x3Icon className="size-4" aria-hidden />
            </span>
            How matrices work
            <BetaBadge />
          </DialogTitle>
          <DialogDescription>
            A matrix runs one analysis per row and collects the results into
            columns.
          </DialogDescription>
        </DialogHeader>

        <div className="grid min-h-0 flex-1 gap-3 overflow-y-auto px-6 py-4 text-sm [scrollbar-width:thin]">
          <InfoCard
            icon={<Table2Icon className="size-4" aria-hidden />}
            title="Rows and columns"
          >
            Every row is one analysis, every column one of its fields. New rows
            join at the grid's bottom left corner and open their first cell
            right away; new columns join at the right end of the header row.
            Click a column's name to rename it or remove it, and drag the line
            between two headers to resize.
          </InfoCard>

          <InfoCard
            icon={<SparklesIcon className="size-4" aria-hidden />}
            title="Analysis steps"
          >
            Columns marked with the star{" "}
            <SparklesIcon
              className="text-primary inline size-3.5 align-[-2px]"
              aria-hidden
            />{" "}
            carry a prompt and are filled in for every row when you start the
            analysis. Type <Key>@</Key> in a prompt to reference other columns.
            Columns without a prompt you fill in yourself, as context for the
            analysis. Adding or saving a column does not start research. Start
            Analysis processes open research cells in the selected rows;
            completed results remain unchanged. Editing existing research
            prompts is temporarily unavailable.
          </InfoCard>

          <InfoCard
            icon={<WandSparklesIcon className="size-4" aria-hidden />}
            title="One question, the whole matrix"
            isComingSoon
          >
            The field beside the matrix's name takes the one question the whole
            matrix answers, and fills the grid in from it: the columns you have
            to provide, the prompts for the ones the analysis produces, and the
            rows to run them on. Everything it proposes stays yours to edit
            before you start the analysis.
          </InfoCard>

          <InfoCard
            icon={<TypeIcon className="size-4" aria-hidden />}
            title="Column types"
          >
            New columns use Text for free-form writing, Markdown included. Use
            the prompt to describe the result the analysis should produce.
          </InfoCard>

          <InfoCard
            icon={<TextCursorInputIcon className="size-4" aria-hidden />}
            title="Editing cells"
          >
            Click a cell to select it, double-click to edit. The editor grows to
            ten lines and scrolls past that, <Key>Ctrl+Enter</Key> breaks a
            line, and clicking anywhere else saves what you typed. Result cells
            are filled by the analysis, so double-clicking one opens it instead.
            Text cells understand Markdown, whether you or the analysis wrote
            it; the expanded view renders it and offers a live preview while
            writing.
          </InfoCard>

          <InfoCard
            icon={<ArrowUpDownIcon className="size-4" aria-hidden />}
            title="Sorting and selecting"
          >
            The small handle at the left edge of every column header sorts the
            grid, and clicking cycles through:
            <span className="mt-1.5 flex flex-wrap gap-1.5">
              <Chip icon={<EqualIcon className="size-3" aria-hidden />}>
                Original order
              </Chip>
              <Chip icon={<ArrowUpIcon className="size-3" aria-hidden />}>
                A to Z
              </Chip>
              <Chip icon={<ArrowDownIcon className="size-3" aria-hidden />}>
                Z to A
              </Chip>
            </span>
            <span className="mt-1.5 block">
              Empty cells always sink to the bottom. Tick rows with the
              checkboxes and the bar that appears above the grid deletes the
              whole selection at once.
            </span>
          </InfoCard>

          <InfoCard
            icon={<KeyboardIcon className="size-4" aria-hidden />}
            title="Keyboard"
          >
            <span className="grid gap-1.5">
              <Shortcut keys={["↑", "↓", "←", "→"]}>
                Move between cells
              </Shortcut>
              <Shortcut keys={["Enter"]}>
                Edit the selected cell, or open a result
              </Shortcut>
              <Shortcut keys={["Enter"]}>
                Save the edit, then one cell down
              </Shortcut>
              <Shortcut keys={["Shift+Enter"]}>Save, then one cell up</Shortcut>
              <Shortcut keys={["Tab", "Shift+Tab"]}>
                Save, then along the row, skipping result cells
              </Shortcut>
              <Shortcut keys={["Ctrl+Enter"]}>
                Line break inside the editor
              </Shortcut>
              <Shortcut keys={["Esc"]}>
                Discard the edit, then clear the selection
              </Shortcut>
            </span>
          </InfoCard>

          <InfoCard
            icon={<Maximize2Icon className="size-4" aria-hidden />}
            title="The full content"
          >
            Cells show three lines before they trim. Hover one and the{" "}
            <Maximize2Icon
              className="inline size-3.5 align-[-2px]"
              aria-hidden
            />{" "}
            button opens it in a side panel with the whole content: text with
            Write and Preview, badges and lists as one entry per row, with the
            matrix's known labels one click away. Saving is explicit, and
            closing with unsaved changes asks first.
          </InfoCard>

          <InfoCard
            icon={<ExternalLinkIcon className="size-4" aria-hidden />}
            title="Every row is a report"
          >
            Hover a row and{" "}
            <ExternalLinkIcon
              className="inline size-3.5 align-[-2px]"
              aria-hidden
            />{" "}
            appears in place of its number and opens that row's full report.
            Rerunning the analysis updates these reports instead of duplicating
            them.
          </InfoCard>

          <InfoCard
            icon={<MoreHorizontalIcon className="size-4" aria-hidden />}
            title="Your matrices"
          >
            The list on the left shows every matrix with a preview of its grid;
            fold it away and the title turns into a switcher with search. The{" "}
            <MoreHorizontalIcon
              className="inline size-3.5 align-[-2px]"
              aria-hidden
            />{" "}
            next to any matrix renames, duplicates or deletes it. Duplicating
            copies columns, rows and results into a matrix of your own and runs
            nothing, so it costs no credits.
          </InfoCard>

          <InfoCard
            icon={<Loader2Icon className="size-4" aria-hidden />}
            title="While it runs"
          >
            Cells waiting their turn show{" "}
            <Badge
              variant="outline"
              className="bg-background text-muted-foreground translate-y-[-1px] text-[10px] font-normal"
            >
              Queued
            </Badge>
            , the one being worked on shows{" "}
            <span className="text-muted-foreground inline-flex translate-y-[2px] items-center gap-1 text-xs">
              <Loader2Icon className="size-3 animate-spin" aria-hidden />
              Running...
            </span>
            , and results land in the grid as they finish. An enriched cell
            stays empty until you start the matrix; adding a column queues
            nothing on its own.
          </InfoCard>

          <p className="rounded-md border border-amber-500/25 bg-amber-500/10 px-3 py-2 text-xs text-amber-900 dark:text-amber-100">
            Matrix is in beta. Columns, prompts and results may still change.
          </p>
        </div>

        <DialogFooter className="border-t px-6 pt-4 pb-6">
          <Button onClick={() => onOpenChange(false)}>Got it</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InfoCard({
  icon,
  title,
  isComingSoon = false,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  // Says so on the card, and steps back a shade from the ones that are built.
  isComingSoon?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        "bg-card flex gap-3 rounded-lg border p-3",
        isComingSoon && "opacity-60",
      )}
    >
      <span
        className={cn(
          "mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-md",
          isComingSoon
            ? "bg-muted text-muted-foreground"
            : "bg-primary/10 text-primary",
        )}
      >
        {icon}
      </span>
      <div className="min-w-0 space-y-1">
        <p className="flex flex-wrap items-center gap-x-1.5 font-medium">
          {title}
          {isComingSoon ? <ComingSoonBadge /> : null}
        </p>
        <p className="text-muted-foreground leading-relaxed">{children}</p>
      </div>
    </div>
  );
}

function Chip({
  icon,
  children,
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <span className="bg-background text-foreground inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-xs">
      <span className="text-muted-foreground">{icon}</span>
      {children}
    </span>
  );
}

function Key({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="bg-muted text-foreground rounded border px-1.5 py-0.5 font-mono text-[11px] font-medium">
      {children}
    </kbd>
  );
}

function Shortcut({
  keys,
  children,
}: {
  keys: string[];
  children: React.ReactNode;
}) {
  return (
    <span className="flex items-baseline gap-2">
      <span className="flex shrink-0 gap-1">
        {keys.map((key) => (
          <Key key={key}>{key}</Key>
        ))}
      </span>
      <span className="min-w-0">{children}</span>
    </span>
  );
}
